import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MessagesSquare, Pencil, SearchX, Trash2 } from 'lucide-react';
import { RowActions } from '@/components/ui/row-actions';
import { cn } from '@/lib/utils';
import { filterSessions, groupSessions, sessionMeta } from './aiModel';
import { SearchField, edgeFadeStyle, useScrollEdges } from './ui';

const VISIBLE_LIMIT = 12;

function RenameInput({ initial, onSave, onCancel }) {
  const [value, setValue] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const save = async () => {
    const title = value.trim();
    if (!title || title === initial) {
      onCancel();
      return;
    }
    setSaving(true);
    const ok = await onSave(title);
    if (!ok) setSaving(false);
  };
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5">
      <input
        ref={ref}
        value={value}
        maxLength={80}
        disabled={saving}
        aria-label="Chat name"
        data-testid="ai-rename-input"
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); save(); }
          if (event.key === 'Escape') { event.preventDefault(); onCancel(); }
        }}
        onBlur={() => { if (!saving) save(); }}
        className="h-8 min-w-0 flex-1 rounded-lg border border-[color:var(--brand-ink)] bg-surface px-2 text-[13px] text-ink outline-none ring-2 ring-[color:var(--brand-soft-border)]"
      />
      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-3" aria-hidden="true" /> : null}
    </div>
  );
}

/**
 * History: search, Today / Yesterday / month groups, ⋯ menu (Rename, Delete…) on hover. No trash icons.
 */
export default function AiHistory({
  sessions, loading, error, currentId, disabled, onOpen, onRename, onDelete, onRetry, className,
}) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [renamingId, setRenamingId] = useState('');
  const listRef = useRef(null);

  const filtered = useMemo(() => filterSessions(sessions, query), [sessions, query]);
  const limited = showAll || query ? filtered : filtered.slice(0, VISIBLE_LIMIT);
  // Keep the open chat visible even when it is older than the limit.
  const current = !showAll && !query && currentId && !limited.some((item) => item.id === currentId)
    ? filtered.find((item) => item.id === currentId)
    : null;
  const groups = useMemo(() => groupSessions(current ? [...limited, current] : limited), [limited, current]);
  const hiddenCount = Math.max(0, filtered.length - limited.length - (current ? 1 : 0));
  // A fade at the edge that hides chats — the hint that the list scrolls.
  const edges = useScrollEdges(listRef, { axis: 'y', watch: `${filtered.length}|${limited.length}|${loading}|${renamingId}` });

  return (
    <section className={cn('flex min-h-0 flex-col rounded-card border border-line bg-surface p-3 shadow-card', className)} aria-label="Chat history" data-testid="ai-history">
      {/* Only the list scrolls: the head, the search box and "Show N older" keep their height. */}
      <div className="flex shrink-0 items-center gap-2 px-1 pb-2.5 pt-1">
        <h2 className="text-[14px] font-bold text-ink">History</h2>
        <span className="text-[12.5px] font-semibold tabular-nums text-ink-3" data-testid="ai-history-count">{sessions.length}</span>
      </div>
      <SearchField
        value={query}
        onChange={setQuery}
        label="Search chats"
        placeholder="Search chats"
        shortcut={false}
        className="h-8 shrink-0 rounded-lg"
        inputClassName="text-[13px]"
        testId="ai-history-search"
      />
      <div
        ref={listRef}
        className="mt-2 min-h-0 flex-1 overflow-y-auto pr-0.5"
        style={edgeFadeStyle(edges, 24, 'y')}
        data-testid="ai-history-list"
        data-scroll-end={edges.end ? 'true' : undefined}
      >
        {loading && !sessions.length ? (
          <div className="flex items-center gap-2 px-2 py-3 text-[12.5px] text-ink-3">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Loading chats…
          </div>
        ) : null}
        {error && !sessions.length ? (
          <div className="px-2 py-3 text-[12.5px] text-ink-3">
            Couldn't load your chats.{' '}
            <button type="button" onClick={onRetry} className="font-semibold text-brand-ink hover:underline">Try again</button>
          </div>
        ) : null}
        {!loading && !error && !sessions.length ? (
          <div className="flex items-start gap-2 px-2 py-3 text-[12.5px] text-ink-3">
            <MessagesSquare className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Your chats appear here, newest first.
          </div>
        ) : null}
        {sessions.length && !filtered.length ? (
          <div className="flex items-start gap-2 px-2 py-3 text-[12.5px] text-ink-3">
            <SearchX className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            No chats match “{query}”.
          </div>
        ) : null}
        {groups.map((group) => (
          <div key={group.key} className="mt-1.5 first:mt-0">
            <p className="px-2 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{group.label}</p>
            <ul className="space-y-0.5">
              {group.items.map((session) => {
                const active = session.id === currentId;
                if (renamingId === session.id) {
                  return (
                    <li key={session.id}>
                      <RenameInput
                        initial={session.title}
                        onCancel={() => setRenamingId('')}
                        onSave={async (title) => {
                          const ok = await onRename(session, title);
                          if (ok) setRenamingId('');
                          return ok;
                        }}
                      />
                    </li>
                  );
                }
                return (
                  <li key={session.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => onOpen(session.id)}
                      disabled={disabled}
                      aria-current={active ? 'true' : undefined}
                      data-testid={`ai-history-item-${session.id}`}
                      className={cn(
                        'block w-full rounded-lg py-[5px] pl-2.5 pr-9 text-left leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed',
                        active
                          ? 'bg-brand-soft shadow-[inset_3px_0_0_var(--brand-ink)]'
                          : 'hover:bg-surface-hover'
                      )}
                    >
                      <span className={cn('block truncate text-[13px]', active ? 'font-semibold text-brand-ink' : 'text-ink')} title={session.title}>
                        {session.title || 'New chat'}
                      </span>
                      <span className="mt-px block truncate text-[11.5px] text-ink-3">{sessionMeta(session)}</span>
                    </button>
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 has-[[data-state=open]]:opacity-100 [@media(hover:none)]:opacity-100">
                      <RowActions
                        label={`Actions for ${session.title || 'chat'}`}
                        data-testid={`ai-history-actions-${session.id}`}
                        disabled={disabled}
                        items={[
                          { label: 'Rename', icon: Pencil, onSelect: () => setRenamingId(session.id), testId: 'ai-history-rename' },
                          { label: 'Delete…', icon: Trash2, danger: true, onSelect: () => onDelete(session), testId: 'ai-history-delete' },
                        ]}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-1 shrink-0 rounded-lg px-2 py-1 text-left text-[12px] font-semibold text-ink-3 hover:bg-surface-hover hover:text-ink-2"
        >
          Show {hiddenCount} older
        </button>
      ) : null}
    </section>
  );
}
